// Wave 3 — Public About page.
// R1d.10-b — Factual Owner biography added: Edwin R. Flamer's construction
// experience began in 2001 at EF1 General Contracting / F Squared LLC; ERF
// ENTERPRISES LLC was founded in 2021. Do NOT collapse those two dates.
// (rev 3 — company name corrected to EF1 General Contracting)
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import PublicShell, { usePublicContent } from "../../components/public/PublicShell";
import SEOHead, { buildLocalBusinessSchema } from "../../components/public/SEOHead";

function safeParseList(value) {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export default function About() {
  const c = usePublicContent();
  const accomplishments = useMemo(
    () => safeParseList(c.owner_accomplishments_json),
    [c.owner_accomplishments_json],
  );
  // Consolidated /about biography pass — 5-part Experience Timeline.
  const experienceTimeline = useMemo(
    () => safeParseList(c.experience_timeline_json),
    [c.experience_timeline_json],
  );
  const founderQuote = (c.founder_quote || "").trim();
  const founderQuoteAttribution = (c.founder_quote_attribution || "").trim();
  // R1d.10-b — Person + Organization JSON-LD reflecting the corrected
  // timeline. Person carries construction experience from 2001; the ERF
  // Organization foundingDate is strictly 2021.
  const jsonLd = useMemo(() => {
    const org = buildLocalBusinessSchema({ content: c, states: [] });
    if (c.erf_founding_year) {
      org.foundingDate = String(c.erf_founding_year);
    }
    return {
      "@context": "https://schema.org",
      "@graph": [
        org,
        {
          "@context": "https://schema.org",
          "@type": "Person",
          "name": "Edwin R. Flamer",
          "jobTitle": "Owner and Project Manager",
          "worksFor": {
            "@type": "GeneralContractor",
            "name": "ERF ENTERPRISES LLC",
            ...(c.erf_founding_year ? { foundingDate: String(c.erf_founding_year) } : {}),
          },
          "description":
            "Edwin R. Flamer's construction-industry experience began in 2001 at " +
            "EF1 General Contracting / F Squared LLC. He founded ERF ENTERPRISES LLC in 2021.",
        },
      ],
    };
  }, [c]);

  const hero = (
    <section className="erf-public-hero" style={{ padding: "72px 0 56px" }} data-testid="about-hero">
      <div className="erf-public-container">
        <div className="erf-public-hero-eyebrow">About</div>
        <h1 style={{ maxWidth: 800 }}>General contracting, built on a standard.</h1>
      </div>
    </section>
  );

  return (
    <PublicShell hero={hero}>
      <SEOHead
        title="About ERF ENTERPRISES LLC — Owner Edwin R. Flamer"
        description={
          "ERF ENTERPRISES LLC was founded in 2021 by Edwin R. Flamer, whose " +
          "construction-industry experience began in 2001. Full-service general " +
          "contracting from Pennsylvania to Florida."
        }
        jsonLd={jsonLd}
      />
      <section className="erf-public-section" data-testid="about-body">
        <div className="erf-public-container" style={{ maxWidth: 820 }}>
          <div className="erf-public-eyebrow">Our mission</div>
          <p style={{ fontSize: 20, lineHeight: 1.6, color: "var(--exec-primary)", margin: "12px 0 40px", fontFamily: '"Playfair Display", serif' }}>
            {c.mission_statement || ""}
          </p>
          <div className="erf-public-eyebrow" style={{ marginTop: 24 }}>Who we are</div>
          <p style={{ fontSize: 17, lineHeight: 1.75, color: "var(--exec-body)", margin: "12px 0 20px" }}>
            {c.about_paragraph_1 || ""}
          </p>
          <p style={{ fontSize: 17, lineHeight: 1.75, color: "var(--exec-body)", margin: "12px 0 32px" }}>
            {c.about_paragraph_2 || ""}
          </p>

          {/* Consolidated /about biography pass — Owner-authorized 9-paragraph
              biography, 5-part Experience Timeline, and Founder quotation. All
              copy is verbatim and MUST NOT be paraphrased or truncated. */}
          {(c.owner_bio_p1 || c.owner_bio_p2) && (
            <div
              style={{ borderTop: "1px solid var(--exec-border)", paddingTop: 32, marginTop: 32 }}
              data-testid="about-the-owner"
            >
              <div className="erf-public-eyebrow">About the Owner</div>
              <h2
                style={{
                  fontFamily: '"Playfair Display", serif',
                  fontSize: 28,
                  lineHeight: 1.25,
                  color: "var(--exec-primary)",
                  margin: "8px 0 20px",
                }}
                data-testid="about-owner-name"
              >
                Edwin R. Flamer — Owner &amp; Project Manager
              </h2>
              {[
                "owner_bio_p1", "owner_bio_p2", "owner_bio_p3", "owner_bio_p4",
                "owner_bio_p5", "owner_bio_p6", "owner_bio_p7", "owner_bio_p8",
                "owner_bio_p9",
              ].map(
                (key) =>
                  c[key] ? (
                    <p
                      key={key}
                      data-testid={`about-owner-${key}`}
                      style={{
                        fontSize: 17,
                        lineHeight: 1.75,
                        color: "var(--exec-body)",
                        margin: "12px 0 18px",
                      }}
                    >
                      {c[key]}
                    </p>
                  ) : null,
              )}

              {founderQuote && (
                <blockquote
                  data-testid="about-founder-quote"
                  style={{
                    margin: "40px 0 32px",
                    padding: "28px 32px",
                    borderLeft: "4px solid #B08D57",
                    background: "rgba(176, 141, 87, 0.06)",
                    borderRadius: "0 8px 8px 0",
                    fontFamily: '"Playfair Display", serif',
                    fontStyle: "italic",
                    fontSize: 22,
                    lineHeight: 1.55,
                    color: "var(--exec-primary)",
                  }}
                >
                  <span data-testid="about-founder-quote-text">&ldquo;{founderQuote}&rdquo;</span>
                  {founderQuoteAttribution && (
                    <footer
                      data-testid="about-founder-quote-attribution"
                      style={{
                        marginTop: 16,
                        fontFamily: "system-ui, -apple-system, sans-serif",
                        fontStyle: "normal",
                        fontSize: 15,
                        letterSpacing: "0.02em",
                        color: "var(--exec-body)",
                      }}
                    >
                      {founderQuoteAttribution}
                    </footer>
                  )}
                </blockquote>
              )}

              {experienceTimeline.length > 0 && (
                <div style={{ marginTop: 32 }} data-testid="about-experience-timeline">
                  <div className="erf-public-eyebrow" style={{ marginBottom: 20 }}>
                    Experience Timeline
                  </div>
                  <ol
                    style={{
                      listStyle: "none",
                      padding: 0,
                      margin: 0,
                      display: "grid",
                      gap: 24,
                    }}
                  >
                    {experienceTimeline.map((stage, idx) => (
                      <li
                        key={stage.n || idx}
                        data-testid={`about-experience-timeline-${stage.n || idx}`}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "72px 1fr",
                          gap: 20,
                          alignItems: "start",
                          padding: "20px 4px 20px 0",
                          borderTop: idx === 0 ? "none" : "1px solid var(--exec-border)",
                        }}
                      >
                        <div
                          data-testid={`about-experience-timeline-${stage.n || idx}-num`}
                          style={{
                            fontFamily: '"Playfair Display", serif',
                            fontSize: 34,
                            lineHeight: 1,
                            color: "#B08D57",
                            fontWeight: 600,
                          }}
                        >
                          {stage.n || String(idx + 1).padStart(2, "0")}
                        </div>
                        <div>
                          <h3
                            data-testid={`about-experience-timeline-${stage.n || idx}-title`}
                            style={{
                              fontFamily: '"Playfair Display", serif',
                              fontSize: 22,
                              lineHeight: 1.3,
                              color: "var(--exec-primary)",
                              margin: "0 0 10px",
                            }}
                          >
                            {stage.title}
                          </h3>
                          <p
                            data-testid={`about-experience-timeline-${stage.n || idx}-body`}
                            style={{
                              fontSize: 16,
                              lineHeight: 1.7,
                              color: "var(--exec-body)",
                              margin: 0,
                            }}
                          >
                            {stage.body}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {accomplishments.length > 0 && (
                <div style={{ marginTop: 28 }} data-testid="about-owner-accomplishments">
                  <div className="erf-public-eyebrow" style={{ marginBottom: 12 }}>
                    Additional Notes
                  </div>
                  <ul
                    style={{
                      listStyle: "disc",
                      paddingLeft: 22,
                      margin: 0,
                      color: "var(--exec-body)",
                      fontSize: 16,
                      lineHeight: 1.7,
                    }}
                  >
                    {accomplishments.map((item, idx) => (
                      <li
                        key={idx}
                        data-testid={`about-owner-accomplishment-${idx}`}
                        style={{ marginBottom: 8 }}
                      >
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div style={{ borderTop: "1px solid var(--exec-border)", paddingTop: 32, marginTop: 32 }}>
            <div className="erf-public-eyebrow">Ready to start?</div>
            <p style={{ fontSize: 17, lineHeight: 1.7, color: "var(--exec-body)", margin: "12px 0 24px" }}>
              Every project begins with a conversation. Tell us what you're working on and we'll give you a straight assessment of scope, timeline, and budget.
            </p>
            <Link to="/quote" className="erf-public-cta" data-testid="about-cta">Request a Quote</Link>
          </div>
        </div>
      </section>
    </PublicShell>
  );
}
